/**
 * PaymentApproval.js — Módulo de Aprobación de Pagos (Capa 3 Anti-Fraude)
 *
 * Flujo:
 *   1. Cualquier usuario propone un pago (facturas + monto + banco)
 *   2. Admin / dueño aprueba o rechaza (no puede aprobar el suyo propio)
 *   3. Solo se ejecuta si está aprobado + comprobante digital obligatorio
 *   4. Montos sobre el umbral → solo el dueño puede aprobar
 *   5. Telegram al dueño en cada propuesta nueva
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getPaymentApprovalSummary,
  getPaymentApprovalConfig,
  updatePaymentApprovalConfig,
  listPaymentProposals,
  createPaymentProposal,
  reviewPaymentProposal,
  executePaymentProposal,
  cancelPaymentProposal,
  getAccountsPayable,
  getPaymentReceiptUrl,
  getSuppliers,
} from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useStores } from '../contexts/StoreContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { formatDateTimeVE } from '../utils/formatters';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (v) =>
  Number(v || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (iso) => formatDateTimeVE(iso);

const STATUS_LABELS = {
  pending:   { label: 'Pendiente',  color: 'bg-yellow-100 text-yellow-800' },
  approved:  { label: 'Aprobado',   color: 'bg-green-100 text-green-700'   },
  rejected:  { label: 'Rechazado',  color: 'bg-red-100 text-red-700'       },
  executed:  { label: 'Ejecutado',  color: 'bg-blue-100 text-blue-700'     },
  cancelled: { label: 'Cancelado',  color: 'bg-gray-100 text-gray-600'     },
};

const BANKS = [
  { key: 'bdv',        label: 'BDV'        },
  { key: 'banesco',    label: 'Banesco'    },
  { key: 'bnc',        label: 'BNC'        },
  { key: 'banplus',    label: 'Banplus'    },
  { key: 'provincial', label: 'Provincial' },
];

const METHODS = [
  'Transferencia', 'Pago Móvil', 'Zelle', 'Efectivo USD',
  'Efectivo VES', 'Cheque',
];

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>
      {s.label}
    </span>
  );
};

// ── Componente principal ──────────────────────────────────────────────────────

const PaymentApproval = () => {
  const { user, isAdmin } = useAuth();
  const { stores } = useStores();
  const { exchangeRate } = useExchangeRate();

  const [tab, setTab] = useState('pending'); // 'pending' | 'history' | 'config'
  const [summary, setSummary] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [config, setConfig] = useState({ approval_threshold_usd: 500, owner_usernames: [] });
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filters for history tab
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStore, setFilterStore] = useState('');

  // Modal states
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);

  // New proposal form
  const [newForm, setNewForm] = useState({
    store_id: '',
    supplier: '',
    invoice_ids: [],
    total_amount_usd: '',
    total_amount_ves: '',
    currency: 'USD',
    proposed_payment_method: '',
    proposed_bank: '',
    notes: '',
  });
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Review form
  const [reviewAction, setReviewAction] = useState('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewError, setReviewError] = useState('');

  // Execute form
  const [receiptFile, setReceiptFile] = useState(null);
  const [execNotes, setExecNotes] = useState('');
  const [execError, setExecError] = useState('');

  // Config form
  const [configDraft, setConfigDraft] = useState({ approval_threshold_usd: 500, owner_usernames: [] });
  const [ownerUsernamesText, setOwnerUsernamesText] = useState('');
  const [configSaving, setConfigSaving] = useState(false);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumData, cfgData] = await Promise.all([
        getPaymentApprovalSummary(),
        getPaymentApprovalConfig(),
      ]);
      setSummary(sumData);
      setConfig(cfgData);
      setConfigDraft(cfgData);
      setOwnerUsernamesText((cfgData.owner_usernames || []).join(', '));

      const params = {};
      if (tab === 'pending') { params.status = 'pending'; }
      else if (tab === 'history') {
        if (filterStatus) params.status = filterStatus;
        if (filterStore) params.store_id = filterStore;
      }
      const listData = await listPaymentProposals(params);
      setProposals(listData.proposals || []);
    } catch (e) {
      console.error('Error loading payment approval data:', e);
    } finally {
      setLoading(false);
    }
  }, [tab, filterStatus, filterStore, refreshKey]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Canonical suppliers list (combobox) ──────────────────────────────────

  const [allSuppliers, setAllSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const supplierInputRef = useRef(null);
  const supplierDropdownRef = useRef(null);

  // Cargar catálogo canónico UNA VEZ cuando abre el modal
  useEffect(() => {
    if (!showNewModal) return;
    getSuppliers()
      .then(data => setAllSuppliers(Array.isArray(data) ? data : []))
      .catch(() => setAllSuppliers([]));
  }, [showNewModal]);

  // Sincronizar texto de búsqueda con el valor seleccionado
  useEffect(() => {
    setSupplierSearch(newForm.supplier || '');
  }, [newForm.supplier]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (
        supplierInputRef.current && !supplierInputRef.current.contains(e.target) &&
        supplierDropdownRef.current && !supplierDropdownRef.current.contains(e.target)
      ) {
        setShowSupplierDropdown(false);
        if (supplierSearch !== newForm.supplier) {
          setSupplierSearch(newForm.supplier || '');
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [supplierSearch, newForm.supplier]);

  const filteredSuppliers = allSuppliers.filter(s =>
    s.name.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  // ── Carga de facturas — UNA llamada por tienda, cacheada en memoria ───────

  const apCacheRef = useRef({});               // storeId → AP response
  const [apDataForStore, setApDataForStore] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState('');

  // Efecto 1: cuando cambia la TIENDA → fetch AP una vez, guarda en caché
  useEffect(() => {
    setApDataForStore(null);
    setAvailableInvoices([]);
    if (!showNewModal || !newForm.store_id) return;

    const storeId = newForm.store_id;

    // Usar caché si ya se cargó esta tienda
    if (apCacheRef.current[storeId]) {
      setApDataForStore(apCacheRef.current[storeId]);
      return;
    }

    let cancelled = false;
    setLoadingInvoices(true);
    getAccountsPayable({ store_id: storeId })
      .then(data => {
        if (cancelled) return;
        apCacheRef.current[storeId] = data;
        setApDataForStore(data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingInvoices(false); });

    return () => { cancelled = true; };
  }, [showNewModal, newForm.store_id]);

  // Efecto 2: cuando cambia el PROVEEDOR → lookup instantáneo desde caché
  // Solo actúa cuando apDataForStore ya está disponible (no sobreescribe el estado de carga)
  useEffect(() => {
    if (!newForm.store_id || !newForm.supplier) {
      setAvailableInvoices([]);
      return;
    }
    if (!apDataForStore) {
      // Datos aún cargando — no cambiar availableInvoices ni loadingInvoices
      return;
    }
    const storeData = apDataForStore[newForm.store_id];
    const supplierData = storeData?.suppliers?.[newForm.supplier];
    setAvailableInvoices(supplierData?.invoices || []);
    setLoadingInvoices(false);
    setInvoiceSearch('');
  }, [apDataForStore, newForm.store_id, newForm.supplier]);

  // ── Toggle invoice selection ──────────────────────────────────────────────

  const toggleInvoice = (inv) => {
    const id = inv.id || inv._id || inv.invoice_number;
    setNewForm(prev => {
      const isSelected = prev.invoice_ids.includes(id);
      const newIds = isSelected
        ? prev.invoice_ids.filter(i => i !== id)
        : [...prev.invoice_ids, id];

      // Auto-sum USD amount from selected invoices
      const selectedInvs = availableInvoices.filter(i =>
        newIds.includes(i.id || i._id || i.invoice_number)
      );
      const totalUsd = selectedInvs.reduce((s, i) => s + (i.total_amount_usd || i.remaining_amount_usd || 0), 0);
      const totalVes = totalUsd * (exchangeRate || 0);

      return {
        ...prev,
        invoice_ids: newIds,
        total_amount_usd: totalUsd > 0 ? totalUsd.toFixed(2) : prev.total_amount_usd,
        total_amount_ves: totalVes > 0 ? totalVes.toFixed(2) : prev.total_amount_ves,
      };
    });
  };

  // ── Crear propuesta ───────────────────────────────────────────────────────

  const handleSubmitProposal = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!newForm.store_id)                  { setFormError('Selecciona una tienda'); return; }
    if (!newForm.supplier)                  { setFormError('Ingresa el proveedor'); return; }
    if (!newForm.total_amount_usd || parseFloat(newForm.total_amount_usd) <= 0)
                                            { setFormError('El monto debe ser mayor a 0'); return; }
    if (!newForm.proposed_payment_method)   { setFormError('Selecciona el método de pago'); return; }
    if (newForm.invoice_ids.length === 0)   { setFormError('Selecciona al menos una factura'); return; }

    setSubmitting(true);
    try {
      await createPaymentProposal({
        store_id:                 newForm.store_id,
        supplier:                 newForm.supplier,
        invoice_ids:              newForm.invoice_ids,
        total_amount_usd:         parseFloat(newForm.total_amount_usd),
        total_amount_ves:         parseFloat(newForm.total_amount_ves) || 0,
        currency:                 newForm.currency,
        proposed_payment_method:  newForm.proposed_payment_method,
        proposed_bank:            newForm.proposed_bank,
        notes:                    newForm.notes,
      });
      setShowNewModal(false);
      setNewForm({
        store_id: '', supplier: '', invoice_ids: [],
        total_amount_usd: '', total_amount_ves: '',
        currency: 'USD', proposed_payment_method: '',
        proposed_bank: '', notes: '',
      });
      setRefreshKey(k => k + 1);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Error al crear la propuesta');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Revisar (aprobar / rechazar) ──────────────────────────────────────────

  const handleReview = async () => {
    setReviewError('');
    try {
      await reviewPaymentProposal(selectedProposal.id, reviewAction, reviewNotes);
      setShowReviewModal(false);
      setSelectedProposal(null);
      setReviewNotes('');
      setRefreshKey(k => k + 1);
    } catch (err) {
      setReviewError(err.response?.data?.detail || 'Error al procesar la revisión');
    }
  };

  // ── Ejecutar ──────────────────────────────────────────────────────────────

  const handleExecute = async () => {
    setExecError('');
    if (!receiptFile) { setExecError('El comprobante es obligatorio'); return; }
    try {
      await executePaymentProposal(selectedProposal.id, receiptFile, execNotes);
      setShowExecuteModal(false);
      setSelectedProposal(null);
      setReceiptFile(null);
      setExecNotes('');
      setRefreshKey(k => k + 1);
    } catch (err) {
      setExecError(err.response?.data?.detail || 'Error al ejecutar el pago');
    }
  };

  // ── Cancelar ─────────────────────────────────────────────────────────────

  const handleCancel = async (proposal) => {
    if (!window.confirm(`¿Cancelar la propuesta ${proposal.proposal_number}?`)) return;
    try {
      await cancelPaymentProposal(proposal.id, 'Cancelada manualmente');
      setRefreshKey(k => k + 1);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cancelar');
    }
  };

  // ── Guardar config ────────────────────────────────────────────────────────

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      const ownerList = ownerUsernamesText
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      await updatePaymentApprovalConfig({
        approval_threshold_usd: parseFloat(configDraft.approval_threshold_usd) || 500,
        owner_usernames:        ownerList,
      });
      setRefreshKey(k => k + 1);
      alert('Configuración guardada');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setConfigSaving(false);
    }
  };

  // ── Helpers de permisos ───────────────────────────────────────────────────

  const canReview = (p) => {
    if (!isAdmin) return false;
    if (p.proposed_by_username === user?.username) return false; // no puede revisar el suyo
    if (p.requires_owner_approval) {
      const owners = p.owner_usernames || [];
      if (owners.length > 0 && !owners.includes(user?.username)) return false;
    }
    return p.status === 'pending';
  };

  const canExecute = (p) => p.status === 'approved';

  const canCancel = (p) =>
    ['pending', 'approved'].includes(p.status) &&
    (isAdmin || p.proposed_by_username === user?.username);

  // ── Render ────────────────────────────────────────────────────────────────

  const storeName = (id) => stores.find(s => s.id === id)?.name || id;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aprobación de Pagos</h1>
          <p className="text-sm text-gray-500 mt-0.5">Control anti-fraude · Segregación de funciones</p>
        </div>
        <button
          onClick={() => { setShowNewModal(true); setFormError(''); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm shadow-sm"
        >
          + Nueva Propuesta
        </button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            label="Pendientes de revisión"
            value={summary.by_status?.pending?.count || 0}
            sub={`$ ${fmt$(summary.total_pending_usd)}`}
            color="yellow"
            icon="⏳"
          />
          <SummaryCard
            label="Aprobados sin ejecutar"
            value={summary.approved_pending_exec || 0}
            sub={`$ ${fmt$(summary.total_approved_usd)}`}
            color="green"
            icon="✅"
          />
          <SummaryCard
            label="Urgentes (>1 día)"
            value={summary.urgent_pending || 0}
            sub="Requieren atención"
            color={summary.urgent_pending > 0 ? 'red' : 'gray'}
            icon="⚠️"
          />
          <SummaryCard
            label="Ejecutados (total)"
            value={summary.by_status?.executed?.count || 0}
            sub={`$ ${fmt$(summary.by_status?.executed?.total_usd || 0)}`}
            color="blue"
            icon="💸"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {[
          { key: 'pending', label: '⏳ Pendientes' },
          { key: 'history', label: '📋 Historial' },
          ...(isAdmin ? [{ key: 'config', label: '⚙️ Configuración' }] : []),
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 mr-2 transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* History filters */}
      {tab === 'history' && (
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select
            value={filterStore}
            onChange={e => setFilterStore(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm"
          >
            <option value="">Todas las tiendas</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button
            onClick={() => { setFilterStatus(''); setFilterStore(''); }}
            className="text-sm text-blue-600 hover:underline"
          >
            Limpiar
          </button>
        </div>
      )}

      {/* Proposals list */}
      {tab !== 'config' && (
        loading ? (
          <div className="text-center py-12 text-gray-400">Cargando...</div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            {tab === 'pending' ? 'No hay propuestas pendientes 🎉' : 'No hay propuestas que mostrar'}
          </div>
        ) : (
          <div className="space-y-3">
            {proposals.map(p => (
              <ProposalCard
                key={p.id}
                proposal={p}
                storeName={storeName(p.store_id)}
                currentUsername={user?.username}
                config={config}
                canReview={canReview(p)}
                canExecute={canExecute(p)}
                canCancel={canCancel(p)}
                onDetail={() => { setSelectedProposal(p); setShowDetailModal(true); }}
                onReview={() => { setSelectedProposal(p); setReviewAction('approve'); setReviewNotes(''); setReviewError(''); setShowReviewModal(true); }}
                onExecute={() => { setSelectedProposal(p); setReceiptFile(null); setExecNotes(''); setExecError(''); setShowExecuteModal(true); }}
                onCancel={() => handleCancel(p)}
              />
            ))}
          </div>
        )
      )}

      {/* Config tab */}
      {tab === 'config' && isAdmin && (
        <div className="max-w-lg bg-white border rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Configuración de Aprobaciones</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Umbral de aprobación exclusiva del dueño (USD)
              </label>
              <input
                type="number"
                value={configDraft.approval_threshold_usd}
                onChange={e => setConfigDraft(d => ({ ...d, approval_threshold_usd: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
                min="0"
                step="50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pagos ≥ este monto solo pueden ser aprobados por los usuarios dueño definidos abajo.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usernames del dueño (separados por coma)
              </label>
              <input
                type="text"
                value={ownerUsernamesText}
                onChange={e => setOwnerUsernamesText(e.target.value)}
                placeholder="karimjah, admin2"
                className="w-full border rounded px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Solo estos usuarios pueden aprobar pagos sobre el umbral.
              </p>
            </div>

            <button
              onClick={handleSaveConfig}
              disabled={configSaving}
              className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50"
            >
              {configSaving ? 'Guardando...' : 'Guardar configuración'}
            </button>
          </div>
        </div>
      )}

      {/* ── Modal: Nueva propuesta ── */}
      {showNewModal && (
        <Modal title="Nueva propuesta de pago" onClose={() => setShowNewModal(false)} wide>
          <form onSubmit={handleSubmitProposal} className="space-y-4">
            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Tienda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tienda *</label>
                <select
                  value={newForm.store_id}
                  onChange={e => setNewForm(f => ({ ...f, store_id: e.target.value, supplier: '', invoice_ids: [] }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                  required
                >
                  <option value="">Seleccionar...</option>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {/* Proveedor — combobox con búsqueda */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor *</label>
                <div className="relative">
                  <input
                    ref={supplierInputRef}
                    type="text"
                    value={supplierSearch}
                    onChange={e => {
                      setSupplierSearch(e.target.value);
                      setShowSupplierDropdown(true);
                      // Si borra el texto, limpiar selección
                      if (!e.target.value) {
                        setNewForm(f => ({ ...f, supplier: '', invoice_ids: [] }));
                      }
                    }}
                    onFocus={() => setShowSupplierDropdown(true)}
                    className="w-full border rounded px-3 py-2 text-sm pr-8"
                    placeholder="Escribir para buscar proveedor…"
                    autoComplete="off"
                    required
                  />
                  {/* Ícono desplegable */}
                  <span
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer select-none"
                    onMouseDown={e => { e.preventDefault(); setShowSupplierDropdown(v => !v); supplierInputRef.current?.focus(); }}
                  >▾</span>
                </div>

                {/* Dropdown */}
                {showSupplierDropdown && (
                  <div
                    ref={supplierDropdownRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto"
                  >
                    {filteredSuppliers.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400 italic">
                        Sin resultados para "{supplierSearch}"
                      </div>
                    ) : (
                      filteredSuppliers.map(s => (
                        <div
                          key={s.id}
                          onMouseDown={e => {
                            e.preventDefault();
                            setNewForm(f => ({ ...f, supplier: s.name, invoice_ids: [] }));
                            setSupplierSearch(s.name);
                            setShowSupplierDropdown(false);
                          }}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-800
                            ${newForm.supplier === s.name ? 'bg-blue-100 font-medium text-blue-900' : 'text-gray-800'}`}
                        >
                          {s.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Facturas disponibles */}
            {newForm.store_id && newForm.supplier && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Facturas pendientes *
                    {loadingInvoices && <span className="ml-2 text-xs text-gray-400">Cargando...</span>}
                    {!loadingInvoices && availableInvoices.length > 0 && (
                      <span className="ml-2 text-xs text-gray-400">({availableInvoices.length} total)</span>
                    )}
                  </label>
                </div>

                {/* Buscador de número de factura */}
                {availableInvoices.length > 0 && (
                  <div className="relative mb-2">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">🔍</span>
                    <input
                      type="text"
                      value={invoiceSearch}
                      onChange={e => setInvoiceSearch(e.target.value)}
                      placeholder="Buscar por número de factura…"
                      className="w-full border rounded pl-7 pr-3 py-1.5 text-sm"
                    />
                    {invoiceSearch && (
                      <button
                        onClick={() => setInvoiceSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                      >✕</button>
                    )}
                  </div>
                )}

                {availableInvoices.length === 0 && !loadingInvoices ? (
                  <p className="text-sm text-gray-400 italic">No hay facturas pendientes para este proveedor.</p>
                ) : (
                  (() => {
                    const filtered = invoiceSearch
                      ? availableInvoices.filter(inv =>
                          String(inv.invoice_number).toLowerCase().includes(invoiceSearch.toLowerCase())
                        )
                      : availableInvoices;
                    return (
                      <div className="border rounded max-h-48 overflow-y-auto divide-y text-sm">
                        {filtered.length === 0 ? (
                          <p className="px-3 py-3 text-sm text-gray-400 italic">
                            Sin resultados para "{invoiceSearch}"
                          </p>
                        ) : (
                          filtered.map((inv) => {
                            const id = inv.id || inv._id || inv.invoice_number;
                            const selected = newForm.invoice_ids.includes(id);
                            return (
                              <label
                                key={id}
                                className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${selected ? 'bg-blue-50' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleInvoice(inv)}
                                  className="rounded"
                                />
                                <span className="flex-1">
                                  <span className="font-medium">Fact. {inv.invoice_number}</span>
                                  {inv.due_date && <span className="ml-2 text-gray-400 text-xs">vence {fmtDate(inv.due_date)}</span>}
                                </span>
                                <span className="text-right">
                                  <span className="font-semibold text-blue-700">$ {fmt$(inv.total_amount_usd)}</span>
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    );
                  })()
                )}
              </div>
            )}

            {/* Monto */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto USD *</label>
                <input
                  type="number"
                  value={newForm.total_amount_usd}
                  onChange={e => setNewForm(f => ({ ...f, total_amount_usd: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto VES</label>
                <input
                  type="number"
                  value={newForm.total_amount_ves}
                  onChange={e => setNewForm(f => ({ ...f, total_amount_ves: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Método y banco */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Método de pago *</label>
                <select
                  value={newForm.proposed_payment_method}
                  onChange={e => setNewForm(f => ({ ...f, proposed_payment_method: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                  required
                >
                  <option value="">Seleccionar...</option>
                  {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banco emisor</label>
                <select
                  value={newForm.proposed_bank}
                  onChange={e => setNewForm(f => ({ ...f, proposed_bank: e.target.value }))}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Sin banco específico</option>
                  {BANKS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                </select>
              </div>
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas / justificación</label>
              <textarea
                value={newForm.notes}
                onChange={e => setNewForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
                rows={2}
                placeholder="Opcional: justifica el pago, urgencia, descuento por pronto pago..."
              />
            </div>

            {/* Alerta umbral */}
            {parseFloat(newForm.total_amount_usd) >= config.approval_threshold_usd && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 rounded flex gap-2">
                <span>⚠️</span>
                <span>
                  Este monto supera el umbral de <strong>$ {fmt$(config.approval_threshold_usd)}</strong>.
                  Solo el dueño podrá aprobarlo.
                </span>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {submitting ? 'Enviando...' : '📤 Proponer pago'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Modal: Detalle ── */}
      {showDetailModal && selectedProposal && (
        <Modal title={`Propuesta ${selectedProposal.proposal_number}`} onClose={() => setShowDetailModal(false)} wide>
          <ProposalDetail proposal={selectedProposal} storeName={storeName(selectedProposal.store_id)} />
        </Modal>
      )}

      {/* ── Modal: Revisar ── */}
      {showReviewModal && selectedProposal && (
        <Modal
          title={`Revisar propuesta ${selectedProposal.proposal_number}`}
          onClose={() => setShowReviewModal(false)}
        >
          <div className="space-y-4">
            <div className="bg-gray-50 rounded p-3 text-sm">
              <p><strong>Proveedor:</strong> {selectedProposal.supplier}</p>
              <p><strong>Monto:</strong> $ {fmt$(selectedProposal.total_amount_usd)}</p>
              <p><strong>Método:</strong> {selectedProposal.proposed_payment_method} {selectedProposal.proposed_bank && `— ${selectedProposal.proposed_bank.toUpperCase()}`}</p>
              <p><strong>Propuesto por:</strong> {selectedProposal.proposed_by_name}</p>
              {selectedProposal.notes && <p><strong>Nota:</strong> {selectedProposal.notes}</p>}
            </div>

            {selectedProposal.requires_owner_approval && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-3 py-2 rounded">
                ⚠️ Monto alto — requiere aprobación del dueño
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Decisión *</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="approve"
                    checked={reviewAction === 'approve'}
                    onChange={() => setReviewAction('approve')}
                  />
                  <span className="text-green-700 font-medium">✅ Aprobar</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="reject"
                    checked={reviewAction === 'reject'}
                    onChange={() => setReviewAction('reject')}
                  />
                  <span className="text-red-700 font-medium">❌ Rechazar</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {reviewAction === 'reject' ? 'Motivo del rechazo *' : 'Observaciones (opcional)'}
              </label>
              <textarea
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                rows={2}
                required={reviewAction === 'reject'}
                placeholder={reviewAction === 'reject' ? 'Explica el motivo...' : 'Opcional...'}
              />
            </div>

            {reviewError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                {reviewError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 text-sm text-gray-600"
              >
                Cancelar
              </button>
              <button
                onClick={handleReview}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
                  reviewAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {reviewAction === 'approve' ? '✅ Confirmar aprobación' : '❌ Confirmar rechazo'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Modal: Ejecutar ── */}
      {showExecuteModal && selectedProposal && (
        <Modal
          title={`Ejecutar pago ${selectedProposal.proposal_number}`}
          onClose={() => setShowExecuteModal(false)}
        >
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
              <p><strong>Proveedor:</strong> {selectedProposal.supplier}</p>
              <p><strong>Monto:</strong> $ {fmt$(selectedProposal.total_amount_usd)}</p>
              <p><strong>Banco:</strong> {selectedProposal.proposed_bank?.toUpperCase() || '—'}</p>
              <p><strong>Método:</strong> {selectedProposal.proposed_payment_method}</p>
              <p><strong>Aprobado por:</strong> {selectedProposal.reviewed_by_name || '—'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comprobante de pago <span className="text-red-600">*</span>
              </label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={e => setReceiptFile(e.target.files[0] || null)}
                className="w-full text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Obligatorio. Sin comprobante no se puede ejecutar el pago.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas de ejecución</label>
              <textarea
                value={execNotes}
                onChange={e => setExecNotes(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                rows={2}
                placeholder="Referencia bancaria, fecha valor, etc..."
              />
            </div>

            {execError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                {execError}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExecuteModal(false)}
                className="px-4 py-2 text-sm text-gray-600"
              >
                Cancelar
              </button>
              <button
                onClick={handleExecute}
                disabled={!receiptFile}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-40"
              >
                💸 Ejecutar pago
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ── Sub-componentes ───────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, sub, color, icon }) => {
  const colors = {
    yellow: 'border-yellow-200 bg-yellow-50',
    green:  'border-green-200 bg-green-50',
    red:    'border-red-200 bg-red-50',
    blue:   'border-blue-200 bg-blue-50',
    gray:   'border-gray-200 bg-gray-50',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.gray}`}>
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-xs text-gray-600 font-medium">{label}</div>
      <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
    </div>
  );
};

const ProposalCard = ({
  proposal: p,
  storeName,
  canReview,
  canExecute,
  canCancel,
  onDetail,
  onReview,
  onExecute,
  onCancel,
}) => {
  const fmtDateTime = (iso) => formatDateTimeVE(iso);

  return (
    <div className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900">{p.proposal_number}</span>
            <StatusBadge status={p.status} />
            {p.requires_owner_approval && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                🔑 Solo dueño
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            <span className="font-medium">{p.supplier}</span>
            <span className="mx-1 text-gray-300">·</span>
            <span>{storeName}</span>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {p.proposed_payment_method}{p.proposed_bank ? ` — ${p.proposed_bank.toUpperCase()}` : ''}
            <span className="mx-1">·</span>
            Propuesto por {p.proposed_by_name}
            <span className="mx-1">·</span>
            {fmtDateTime(p.proposed_at)}
          </div>
          {p.invoices_detail?.length > 0 && (
            <div className="text-xs text-gray-400 mt-0.5">
              {p.invoices_detail.length} factura{p.invoices_detail.length > 1 ? 's' : ''}
              {p.review_notes && p.status === 'rejected' && (
                <span className="ml-2 text-red-500">Rechazado: {p.review_notes}</span>
              )}
            </div>
          )}
        </div>

        <div className="text-right shrink-0">
          <div className="text-xl font-bold text-gray-900">$ {Number(p.total_amount_usd || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</div>
          {p.total_amount_ves > 0 && (
            <div className="text-xs text-gray-500">Bs {Number(p.total_amount_ves).toLocaleString('es-VE', { minimumFractionDigits: 2 })}</div>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-3 flex-wrap">
        <button
          onClick={onDetail}
          className="text-xs px-3 py-1.5 border rounded-lg text-gray-600 hover:bg-gray-50"
        >
          Ver detalle
        </button>
        {canReview && (
          <button
            onClick={onReview}
            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Aprobar / Rechazar
          </button>
        )}
        {canExecute && (
          <button
            onClick={onExecute}
            className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            💸 Ejecutar
          </button>
        )}
        {p.status === 'executed' && p.receipt_file_id && (
          <a
            href={getPaymentReceiptUrl(p.id)}
            target="_blank"
            rel="noreferrer"
            className="text-xs px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50"
          >
            📎 Comprobante
          </a>
        )}
        {canCancel && (
          <button
            onClick={onCancel}
            className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
          >
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
};

const ProposalDetail = ({ proposal: p, storeName }) => (
  <div className="space-y-4 text-sm">
    <div className="grid grid-cols-2 gap-3">
      <Detail label="N° Propuesta" value={p.proposal_number} />
      <Detail label="Estado" value={<StatusBadge status={p.status} />} />
      <Detail label="Tienda" value={storeName} />
      <Detail label="Proveedor" value={p.supplier} />
      <Detail label="Monto USD" value={`$ ${Number(p.total_amount_usd).toLocaleString('es-VE', { minimumFractionDigits: 2 })}`} bold />
      {p.total_amount_ves > 0 && <Detail label="Monto VES" value={`Bs ${Number(p.total_amount_ves).toLocaleString('es-VE', { minimumFractionDigits: 2 })}`} />}
      <Detail label="Método" value={`${p.proposed_payment_method}${p.proposed_bank ? ' — ' + p.proposed_bank.toUpperCase() : ''}`} />
      <Detail label="Propuesto por" value={p.proposed_by_name} />
      <Detail label="Fecha propuesta" value={formatDateTimeVE(p.proposed_at)} />
      {p.reviewed_by_name && <Detail label="Revisado por" value={p.reviewed_by_name} />}
      {p.reviewed_at && <Detail label="Fecha revisión" value={formatDateTimeVE(p.reviewed_at)} />}
      {p.review_notes && <Detail label="Nota revisión" value={p.review_notes} />}
      {p.executed_by_name && <Detail label="Ejecutado por" value={p.executed_by_name} />}
      {p.executed_at && <Detail label="Fecha ejecución" value={formatDateTimeVE(p.executed_at)} />}
      {p.execution_notes && <Detail label="Nota ejecución" value={p.execution_notes} />}
    </div>

    {p.notes && (
      <div>
        <span className="font-medium text-gray-700">Justificación: </span>
        <span className="text-gray-600">{p.notes}</span>
      </div>
    )}

    {p.invoices_detail?.length > 0 && (
      <div>
        <p className="font-medium text-gray-700 mb-2">Facturas incluidas:</p>
        <div className="border rounded divide-y text-xs">
          {p.invoices_detail.map((inv, i) => (
            <div key={i} className="px-3 py-2 flex justify-between">
              <span>Fact. {inv.invoice_number}</span>
              {inv.due_date && <span className="text-gray-400">Vence {inv.due_date}</span>}
              <span className="font-semibold">$ {Number(inv.total_amount_usd || 0).toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {p.status === 'executed' && p.receipt_file_id && (
      <a
        href={getPaymentReceiptUrl(p.id)}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"
      >
        📎 Ver comprobante: {p.receipt_filename}
      </a>
    )}
  </div>
);

const Detail = ({ label, value, bold }) => (
  <div>
    <span className="text-gray-500">{label}: </span>
    <span className={bold ? 'font-bold text-gray-900' : 'text-gray-800'}>{value}</span>
  </div>
);

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className={`bg-white rounded-2xl shadow-xl ${wide ? 'w-full max-w-2xl' : 'w-full max-w-lg'} max-h-[90vh] flex flex-col`}>
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
      </div>
      <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
    </div>
  </div>
);

export default PaymentApproval;
